(function () {
  if (window.BaureliaCustomizerCatalog) return;

  const state = {
    bootstrap: null,
    motifs: new Map(),
    loadingMotifs: new Map(),
    motifAssets: new Map(),
    motifRenderId: 0,
    eventsInitialized: false,
    motif: {
      required: false,
      status: "idle",
      requestId: 0,
      controller: null,
      promise: Promise.resolve(null),
      selection: null,
      error: null,
    },
  };

  const configElement = document.getElementById("baurelia-customizer-config");
  let config = {
    proxyBase: "/apps/baurelia-designer",
    defaultColor: "weiss",
    hiddenColors: [],
  };

  if (configElement) {
    try {
      config = { ...config, ...JSON.parse(configElement.textContent) };
    } catch (error) {
      console.error("Baurelia Designer: Konfiguration ist ungültig.", error);
    }
  }

  if (!Array.isArray(config.hiddenColors)) config.hiddenColors = [];

  const valueOf = (item, camelCase, snakeCase) =>
    item[camelCase] ?? item[snakeCase];

  const sortItems = (items) =>
    [...items].sort((left, right) => {
      const leftOrder = Number(valueOf(left, "sortOrder", "sort_order") || 0);
      const rightOrder = Number(valueOf(right, "sortOrder", "sort_order") || 0);
      return leftOrder - rightOrder || String(left.name).localeCompare(String(right.name), "de");
    });

  const uniqueMotifs = (items) => {
    const seen = new Set();
    return items.filter((motif) => {
      const key = `${motif.slug || motif.handle || ""}|${
        valueOf(motif, "imageUrl", "image_url") || ""
      }`;
      if (seen.has(key)) {
        console.warn("Baurelia Designer: Doppeltes Motiv wird ausgeblendet.", motif);
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  const wait = (milliseconds) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const requestWithRetry = async (
    url,
    { signal, accept = "application/json", cache = "no-store", attempts = 3 } = {},
  ) => {
    let lastError;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      if (signal?.aborted) throw new DOMException("Abgebrochen", "AbortError");

      const controller = new AbortController();
      const abortRequest = () => controller.abort();
      signal?.addEventListener("abort", abortRequest, { once: true });
      const timeout = window.setTimeout(() => controller.abort(), 20000);

      try {
        const response = await fetch(url, {
          cache,
          credentials: "same-origin",
          headers: { Accept: accept },
          signal: controller.signal,
        });
        if (response.ok) return response;

        const error = new Error(`HTTP ${response.status}`);
        error.status = response.status;
        if (response.status !== 429 && response.status < 500) throw error;
        lastError = error;
      } catch (error) {
        if (signal?.aborted) throw new DOMException("Abgebrochen", "AbortError");
        if (error?.name === "AbortError" && attempt === attempts - 1) {
          throw new Error("Zeitüberschreitung beim Laden des Motivs.");
        }
        lastError = error;
      } finally {
        window.clearTimeout(timeout);
        signal?.removeEventListener("abort", abortRequest);
      }

      if (attempt < attempts - 1) {
        await wait(300 * 2 ** attempt + Math.floor(Math.random() * 150));
      }
    }

    throw lastError || new Error("Anfrage fehlgeschlagen.");
  };

  const requestJson = async (url) => {
    const response = await requestWithRetry(url, { cache: "default" });
    return response.json();
  };

  const setStatus = (container, message, isError) => {
    container.replaceChildren();
    const status = document.createElement("div");
    status.className = "baurelia-catalog-status";
    if (isError) status.classList.add("baurelia-catalog-status--error");
    status.textContent = message;
    container.append(status);
  };

  const setInputValue = (id, value = "") => {
    const input = document.getElementById(id);
    if (input) input.value = value || "";
  };

  const updateMotifProperties = (selection) => {
    setInputValue("baurelia-motif-id", selection?.id);
    setInputValue("baurelia-motif-handle", selection?.handle);
    setInputValue("baurelia-motif-slug", selection?.slug);
    setInputValue("baurelia-motif-image", selection?.imageUrl);
    setInputValue("baurelia-motif-version", selection?.updatedAt);
  };

  const clearMotifPreview = () => {
    document.querySelectorAll('[id="dynamic-image"]').forEach((image) => {
      image.removeAttribute("href");
      image.removeAttribute("xlink:href");
      delete image.dataset.baureliaMotifId;
    });

    const canvas = document.getElementById("canvas-image");
    const context = canvas?.getContext?.("2d");
    if (canvas && context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      delete canvas.dataset.baureliaMotifId;
    }
  };

  const setMotifOrderButton = (enabled) => {
    if (!state.motif.required) return;
    const button = document.getElementById("generate-pdf-link");
    if (!button) return;
    button.disabled = !enabled;
    button.setAttribute("aria-disabled", enabled ? "false" : "true");
    button.style.pointerEvents = enabled ? "auto" : "none";
    button.style.opacity = enabled ? "1" : "0.55";
  };

  const renderPreviewMotifStatus = (status, message) => {
    let notice = document.getElementById("baurelia-preview-load-status");
    const isMobile = window.matchMedia("(max-width: 766px)").matches;

    if (!isMobile || status === "ready" || status === "idle") {
      notice?.remove();
      return;
    }

    if (!notice) {
      notice = document.createElement("div");
      notice.id = "baurelia-preview-load-status";
      notice.className = "baurelia-preview-load-status";
      document.body.append(notice);
    }

    notice.classList.toggle("baurelia-preview-load-status--error", status === "error");
    notice.setAttribute("role", status === "error" ? "alert" : "status");
    notice.setAttribute("aria-live", status === "error" ? "assertive" : "polite");
    notice.replaceChildren();

    if (status === "loading") {
      const spinner = document.createElement("span");
      spinner.className = "baurelia-preview-spinner";
      spinner.setAttribute("aria-hidden", "true");
      notice.append(spinner);
    }

    const text = document.createElement("span");
    text.textContent = message;
    notice.append(text);

    if (status === "error") {
      const retry = document.createElement("button");
      retry.type = "button";
      retry.dataset.baureliaMotifRetry = "true";
      retry.textContent = "Erneut";
      notice.append(retry);
    }
  };

  const renderMotifLoadStatus = (status, message) => {
    renderPreviewMotifStatus(status, message);
    document.querySelectorAll("[data-baurelia-motifs]").forEach((container) => {
      container.setAttribute("aria-busy", status === "loading" ? "true" : "false");
      container.querySelector(".baurelia-motif-load-status")?.remove();
      if (status === "ready" || status === "idle") return;

      const notice = document.createElement("div");
      notice.className = `baurelia-motif-load-status baurelia-catalog-status${
        status === "error" ? " baurelia-catalog-status--error" : ""
      }`;
      notice.setAttribute("role", status === "error" ? "alert" : "status");
      notice.textContent = message;

      if (status === "error") {
        const retry = document.createElement("button");
        retry.type = "button";
        retry.dataset.baureliaMotifRetry = "true";
        retry.textContent = "Erneut versuchen";
        notice.append(" ", retry);
      }

      container.append(notice);
    });
  };

  const setMotifState = (status, { selection = state.motif.selection, error = null } = {}) => {
    state.motif.status = status;
    state.motif.selection = selection;
    state.motif.error = error;
    setMotifOrderButton(status === "ready");

    const messages = {
      loading: "Motiv wird geladen …",
      error: "Motiv konnte nicht geladen werden. Bitte erneut versuchen.",
    };
    renderMotifLoadStatus(status, messages[status] || "");
    document.dispatchEvent(
      new CustomEvent("baurelia:motif-state", {
        detail: { status, selection, error: error?.message || null },
      }),
    );
  };

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Motivdatei konnte nicht gelesen werden."));
      reader.readAsDataURL(blob);
    });

  const decodeImage = (dataUrl) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Motivdatei ist kein gültiges Bild."));
      image.src = dataUrl;
    });

  const rememberMotifAsset = (url, promise) => {
    state.motifAssets.delete(url);
    state.motifAssets.set(url, promise);

    while (state.motifAssets.size > 8) {
      const oldestUrl = state.motifAssets.keys().next().value;
      if (!oldestUrl || oldestUrl === url) break;
      state.motifAssets.delete(oldestUrl);
    }
  };

  const loadMotifAsset = (url) => {
    if (!url) return Promise.reject(new Error("Motiv-Bild-URL fehlt."));

    const cached = state.motifAssets.get(url);
    if (cached) {
      rememberMotifAsset(url, cached);
      return cached;
    }

    const promise = (async () => {
      const response = await requestWithRetry(url, {
        accept: "image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8",
        cache: "default",
      });
      const dataUrl = await blobToDataUrl(await response.blob());
      const image = await decodeImage(dataUrl);
      return { dataUrl, image };
    })().catch((error) => {
      if (state.motifAssets.get(url) === promise) state.motifAssets.delete(url);
      throw error;
    });

    rememberMotifAsset(url, promise);
    return promise;
  };

  const waitForMotifAsset = (url, signal) =>
    new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException("Abgebrochen", "AbortError"));
        return;
      }

      const abort = () => reject(new DOMException("Abgebrochen", "AbortError"));
      signal.addEventListener("abort", abort, { once: true });
      loadMotifAsset(url).then(
        (asset) => {
          signal.removeEventListener("abort", abort);
          resolve(asset);
        },
        (error) => {
          signal.removeEventListener("abort", abort);
          reject(error);
        },
      );
    });

  const drawMotifCanvas = (image, motifId) => {
    const canvas = document.getElementById("canvas-image");
    if (!canvas) return false;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Motiv-Canvas ist nicht verfügbar.");
    const minimumWidth = 650;
    const aspectRatio = image.width / image.height || 1;
    canvas.width = Math.max(image.width, minimumWidth);
    canvas.height = canvas.width / aspectRatio;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    canvas.dataset.baureliaMotifId = motifId;
    return true;
  };

  const applyMotifImage = (dataUrl, image, motifId) => {
    const svgImages = document.querySelectorAll('[id="dynamic-image"]');
    svgImages.forEach((svgImage) => {
      svgImage.setAttribute("href", dataUrl);
      svgImage.dataset.baureliaMotifId = motifId;
    });
    const canvasUpdated = drawMotifCanvas(image, motifId);
    if (!svgImages.length && !canvasUpdated) {
      throw new Error("Motiv-Vorschau ist für dieses Produkt nicht vorhanden.");
    }
  };

  const updateMotifPreview = async (input) => {
    if (!input) {
      setMotifState("error", {
        selection: null,
        error: new Error("Kein Motiv ausgewählt."),
      });
      return null;
    }
    document.querySelectorAll(".image-swatch").forEach((swatch) => {
      swatch.classList.toggle("active", swatch.contains(input));
    });

    const output = document.getElementById("motiv-value");
    if (output) output.textContent = input.value;

    const selection = {
      id: input.dataset.metaobjectId,
      handle: input.dataset.handle,
      slug: input.dataset.slug,
      displayName: input.value,
      imageUrl: input.dataset.image,
      updatedAt: input.dataset.updatedAt,
    };
    updateMotifProperties(selection);

    const requestId = ++state.motif.requestId;
    state.motif.controller?.abort();
    const controller = new AbortController();
    state.motif.controller = controller;
    setMotifState("loading", { selection });

    const promise = (async () => {
      try {
        if (!selection.id || !selection.imageUrl) {
          throw new Error("Motiv-ID oder Bild-URL fehlt.");
        }
        const { dataUrl, image } = await waitForMotifAsset(
          selection.imageUrl,
          controller.signal,
        );

        if (
          requestId !== state.motif.requestId ||
          controller.signal.aborted ||
          !input.checked
        ) {
          throw new DOMException("Veraltete Motivanfrage", "AbortError");
        }

        applyMotifImage(dataUrl, image, selection.id);
        setMotifState("ready", { selection });
        return selection;
      } catch (error) {
        if (error?.name === "AbortError") return null;
        clearMotifPreview();
        setMotifState("error", { selection, error });
        console.error("Baurelia Designer: Motiv konnte nicht geladen werden.", error);
        return null;
      } finally {
        if (state.motif.controller === controller) state.motif.controller = null;
      }
    })();

    state.motif.promise = promise;
    return promise;
  };

  const createMotif = (motif, index, displayLabel) => {
    const label = document.createElement("label");
    label.className = `image-swatch${index === 0 ? " active" : ""}`;

    const input = document.createElement("input");
    input.className = "image-swatch-input";
    input.type = "radio";
    input.name = "properties[Motiv]";
    input.value = displayLabel;
    input.dataset.name = "Motiv";
    input.dataset.metaobjectId = motif.id || "";
    input.dataset.handle = motif.handle || "";
    input.dataset.slug = motif.slug || motif.handle || "";
    input.dataset.image = valueOf(motif, "imageUrl", "image_url");
    input.dataset.updatedAt = motif.updatedAt || "";
    input.required = true;
    input.checked = index === 0;

    const image = document.createElement("img");
    image.className = "image-swatch-img";
    image.src =
      valueOf(motif, "thumbnailUrl", "thumbnail_url") || input.dataset.image;
    image.alt = valueOf(motif, "altText", "alt_text") || displayLabel;
    image.loading = index < 6 ? "eager" : "lazy";
    image.decoding = "async";
    image.fetchPriority = index === 0 ? "high" : index < 6 ? "auto" : "low";
    image.width = 100;
    image.height = 100;

    const warmMotifAsset = () => {
      loadMotifAsset(input.dataset.image).catch(() => {});
    };
    label.addEventListener("pointerenter", warmMotifAsset, { once: true });
    label.addEventListener("pointerdown", warmMotifAsset, { once: true });
    input.addEventListener("focus", warmMotifAsset, { once: true });

    const note = document.createElement("div");
    note.className = "note-card";
    const corner = document.createElement("div");
    corner.className = "note-corner";
    const text = document.createElement("span");
    text.className = "note-txt";
    text.textContent = displayLabel;
    note.append(corner, text);
    label.append(input, image, note);
    return label;
  };

  const renderMotifs = async (categoryHandle) => {
    const containers = document.querySelectorAll("[data-baurelia-motifs]");
    if (!containers.length || !categoryHandle) return;
    state.motif.required = true;
    const renderId = ++state.motifRenderId;
    state.motif.controller?.abort();
    state.motif.requestId += 1;
    state.motif.selection = null;
    updateMotifProperties(null);
    setMotifState("loading", { selection: null });
    containers.forEach((container) =>
      setStatus(container, "Motive werden geladen …", false),
    );

    try {
      let motifs = state.motifs.get(categoryHandle);
      if (!motifs) {
        let request = state.loadingMotifs.get(categoryHandle);
        if (!request) {
          request = requestJson(
            `${config.proxyBase}/motifs?category=${encodeURIComponent(categoryHandle)}`,
          );
          state.loadingMotifs.set(categoryHandle, request);
          request.then(
            () => {
              if (state.loadingMotifs.get(categoryHandle) === request) {
                state.loadingMotifs.delete(categoryHandle);
              }
            },
            () => {
              if (state.loadingMotifs.get(categoryHandle) === request) {
                state.loadingMotifs.delete(categoryHandle);
              }
            },
          );
        }
        const payload = await request;
        motifs = uniqueMotifs(sortItems(payload.motifs || []));
        state.motifs.set(categoryHandle, motifs);
      }

      if (renderId !== state.motifRenderId) return;

      containers.forEach((container) => {
        container.replaceChildren();
        const wrapper = document.createElement("div");
        wrapper.className = "image-swatch-wrapper";
        wrapper.id = categoryHandle;
        wrapper.style.display = "grid";
        const category = (state.bootstrap?.categories || []).find(
          (item) => item.handle === categoryHandle,
        );
        const categoryName = category?.name || category?.displayName || categoryHandle;
        motifs.forEach((motif, index) =>
          wrapper.append(createMotif(motif, index, `${categoryName} ${index + 1}`)),
        );
        container.append(wrapper);
        if (!motifs.length) setStatus(container, "Keine Motive in dieser Kategorie.", false);
      });

      const checked = document.querySelector('input[name="properties[Motiv]"]:checked');
      if (checked) {
        updateMotifPreview(checked);
        document.dispatchEvent(
          new CustomEvent("baurelia:motifs-rendered", {
            detail: { category: categoryHandle },
          }),
        );
      } else {
        setMotifState("error", {
          selection: null,
          error: new Error("Diese Kategorie enthält keine Motive."),
        });
      }
    } catch (error) {
      if (renderId !== state.motifRenderId) return;
      containers.forEach((container) =>
        setStatus(container, "Motive konnten nicht geladen werden.", true),
      );
      setMotifState("error", { selection: null, error });
      console.error("Baurelia Designer: Motivliste konnte nicht geladen werden.", error);
    }
  };

  const renderCategories = (categories) => {
    const sorted = sortItems(categories);
    document.querySelectorAll("[data-baurelia-categories]").forEach((root) => {
      const select = root.querySelector("select");
      if (!select) return;
      select.replaceChildren();
      sorted.forEach((category) => {
        const option = document.createElement("option");
        option.value = category.handle;
        option.textContent = category.name || category.displayName;
        select.append(option);
      });
      select.disabled = !sorted.length;
      select.setAttribute("aria-busy", "false");
      if (sorted.length) renderMotifs(select.value || sorted[0].handle);
    });
  };

  const fontCssValue = (value) => String(value || "").replace(/["'\\]/g, "");

  const applyFont = (input) => {
    if (!input) return;
    const family = input.dataset.fontFamily || input.dataset.value;
    const scale = Number(input.getAttribute("font-size") || 1);
    const preview = document.getElementById("text-preview-3");
    const basePreviewSize = preview
      ? Number(preview.dataset.baureliaBaseFontSize || parseFloat(window.getComputedStyle(preview).fontSize) || 16)
      : 16;
    input.setAttribute("default-font-size", String(basePreviewSize * scale));
    const defaultSizeInput = document.getElementById("text-size-default");
    if (defaultSizeInput) defaultSizeInput.value = `${Math.floor(basePreviewSize * scale)}px`;
    document
      .querySelectorAll(".font_item, #text-preview, #text-preview-2, #text-preview-3")
      .forEach((element) => {
        if (!element.dataset.baureliaBaseFontSize) {
          element.dataset.baureliaBaseFontSize = String(
            parseFloat(window.getComputedStyle(element).fontSize) || 16,
          );
        }
        element.style.fontFamily = `"${fontCssValue(family)}"`;
        element.style.fontSize = `${Number(element.dataset.baureliaBaseFontSize) * scale}px`;
      });

    document.querySelectorAll(".mw-r_button").forEach((button) => {
      button.classList.toggle("active", button.contains(input));
    });
    document.querySelectorAll("[data-baurelia-fonts]").forEach((root) => {
      const current = root.querySelector(".mw-r_current-value");
      if (current) {
        current.textContent =
          input.closest(".mw-r_button")?.querySelector("label span")?.textContent ||
          input.value;
        current.style.fontFamily = `"${fontCssValue(family)}"`;
      }
    });
    const output = document.getElementById("font-value");
    if (output) output.textContent = input.value;
  };

  const renderFonts = (fonts) => {
    const sorted = sortItems(fonts);
    let style = document.getElementById("baurelia-customizer-font-faces");
    if (!style) {
      style = document.createElement("style");
      style.id = "baurelia-customizer-font-faces";
      document.head.append(style);
    }
    style.textContent = sorted
      .map((font) => {
        const family = fontCssValue(valueOf(font, "fontFamily", "font_family") || font.slug);
        const woff2Url = fontCssValue(valueOf(font, "woff2Url", "woff2_url"));
        const ttfUrl = fontCssValue(valueOf(font, "ttfUrl", "ttf_url"));
        const legacyUrl = fontCssValue(valueOf(font, "fontUrl", "font_url"));
        const sources = [];
        if (woff2Url) sources.push(`url("${woff2Url}") format("woff2")`);
        if (ttfUrl) sources.push(`url("${ttfUrl}") format("truetype")`);
        if (!sources.length && legacyUrl) {
          const legacyFormat = /\.woff2(?:\?|$)/i.test(legacyUrl)
            ? "woff2"
            : /\.ttf(?:\?|$)/i.test(legacyUrl)
              ? "truetype"
              : "woff";
          sources.push(`url("${legacyUrl}") format("${legacyFormat}")`);
        }
        return sources.length
          ? `@font-face{font-family:"${family}";src:${sources.join(",")};font-display:swap;}`
          : "";
      })
      .join("");

    document.querySelectorAll("[data-baurelia-fonts]").forEach((root) => {
      const button = root.querySelector(".mw-custom-select__btn");
      const list = root.querySelector(".mw-custom-select__listbox");
      if (!button || !list) return;
      list.replaceChildren();
      sorted.forEach((font, index) => {
        const family = valueOf(font, "fontFamily", "font_family") || font.slug;
        const item = document.createElement("li");
        item.className = "mw-custom-select__option";
        item.setAttribute("role", "option");
        const wrapper = document.createElement("div");
        wrapper.className = `mw-r_button${index === 0 ? " active" : ""}`;
        const input = document.createElement("input");
        input.className = "mw-r_input";
        input.type = "radio";
        input.name = "properties[Schriftart]";
        input.value = font.slug;
        input.dataset.name = "schriftart";
        input.dataset.value = font.slug;
        input.dataset.fontFamily = family;
        input.setAttribute("font-size", String(font.scale || 1));
        input.required = true;
        input.checked = index === 0;
        const label = document.createElement("label");
        const preview = document.createElement("span");
        preview.textContent = valueOf(font, "previewText", "preview_text") || "Wunschtext";
        preview.style.fontFamily = `"${fontCssValue(family)}"`;
        label.append(preview);
        const value = document.createElement("span");
        value.className = "mw-r_value";
        value.textContent = font.name;
        wrapper.append(input, label, value);
        item.append(wrapper);
        list.append(item);
      });
      button.disabled = !sorted.length;
      button.setAttribute("aria-busy", "false");
    });
    applyFont(document.querySelector('input[name="properties[Schriftart]"]:checked'));
  };

  const applyColor = (input) => {
    if (!input) return;
    const color = input.dataset.color;
    const outputIds = {
      "properties[Schriftfarbe]": ["schriftfarbe", "color-value"],
      "properties[Dekofarbe-1]": ["dekofarbe-1"],
      "properties[Dekofarbe-2]": ["dekofarbe-2"],
    };

    if (input.name === "properties[Schriftfarbe]") {
      document.querySelectorAll(".text-preview, .font_item").forEach((element) => {
        element.style.color = color;
      });
    }
    if (input.name === "properties[Dekofarbe-1]") {
      document.querySelectorAll(".deko-color-1").forEach((element) => {
        element.style.fill = color;
      });
    }
    if (input.name === "properties[Dekofarbe-2]") {
      document.querySelectorAll(".deko-color-2").forEach((element) => {
        element.style.fill = color;
      });
    }

    (outputIds[input.name] || []).forEach((id) => {
      const output = document.getElementById(id);
      if (output) output.textContent = input.value;
    });
  };

  const renderColors = (colors) => {
    const hidden = new Set(config.hiddenColors);
    const sorted = sortItems(colors).filter((color) => !hidden.has(color.slug));
    document.querySelectorAll("[data-baurelia-colors]").forEach((root) => {
      const grid = root.querySelector(".color-picker__grid");
      if (!grid) return;
      const kind = root.dataset.baureliaColors || "text";
      const property =
        root.dataset.colorProperty ||
        (kind === "text" ? "Schriftfarbe" : "Dekofarbe-1");
      const defaultColor = root.dataset.defaultColor || config.defaultColor;
      const available = sorted.filter((color) => {
        const usage = color.usage || "both";
        return usage === "both" || usage === kind;
      });
      grid.replaceChildren();
      available.forEach((color, index) => {
        const item = document.createElement("div");
        item.className = "color-picker__item";
        const swatch = document.createElement("div");
        swatch.className = "color-picker__color";
        swatch.style.backgroundColor = valueOf(color, "hexValue", "hex_value");
        const input = document.createElement("input");
        input.className = "color-picker__input";
        input.type = "radio";
        input.name = `properties[${property}]`;
        input.value = color.slug;
        input.dataset.color = valueOf(color, "hexValue", "hex_value");
        input.required = true;
        input.checked =
          color.slug === defaultColor ||
          (index === 0 && !available.some((entry) => entry.slug === defaultColor));
        const label = document.createElement("label");
        label.className = "color-picker__label";
        label.setAttribute("aria-label", color.name);
        item.append(swatch, input, label);
        grid.append(item);
      });
      grid.setAttribute("aria-busy", "false");
      applyColor(grid.querySelector("input:checked"));
    });
  };

  const initEvents = () => {
    if (state.eventsInitialized) return;
    state.eventsInitialized = true;
    document.addEventListener("change", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      if (target.matches("[data-baurelia-categories] select")) renderMotifs(target.value);
      if (target.matches('input[name="properties[Motiv]"]')) updateMotifPreview(target);
      if (target.matches('input[name="properties[Schriftart]"]')) applyFont(target);
      if (
        target.matches(
          'input[name="properties[Schriftfarbe]"], input[name="properties[Dekofarbe-1]"], input[name="properties[Dekofarbe-2]"]',
        )
      ) {
        applyColor(target);
      }
    });

    document.addEventListener("click", (event) => {
      const button = event.target.closest("[data-baurelia-fonts] .mw-custom-select__btn");
      if (button) {
        const list = button.closest("[data-baurelia-fonts]").querySelector(".mw-custom-select__listbox");
        list.style.display = list.style.display === "grid" ? "none" : "grid";
        return;
      }
      const option = event.target.closest("[data-baurelia-fonts] .mw-custom-select__option");
      if (option) {
        const list = option.closest(".mw-custom-select__listbox");
        list.style.display = "none";
        return;
      }

      const retry = event.target.closest("[data-baurelia-motif-retry]");
      if (retry) {
        const checked = document.querySelector('input[name="properties[Motiv]"]:checked');
        if (checked) {
          updateMotifPreview(checked);
          return;
        }
        const category = document.querySelector("[data-baurelia-categories] select")?.value;
        if (category) renderMotifs(category);
        else init();
      }
    });
  };

  const init = async () => {
    initEvents();
    state.motif.required = Boolean(document.querySelector("[data-baurelia-motifs]"));
    if (state.motif.required) {
      setMotifState("loading", { selection: null });
    }
    try {
      state.bootstrap = await requestJson(`${config.proxyBase}/bootstrap`);
      renderCategories(state.bootstrap.categories || []);
      renderColors(state.bootstrap.colors || []);
      renderFonts(state.bootstrap.fonts || []);
      document.dispatchEvent(
        new CustomEvent("baurelia:catalog-ready", { detail: state.bootstrap }),
      );
    } catch (error) {
      document.querySelectorAll("[data-baurelia-motifs]").forEach((container) =>
        setStatus(container, "Customizer-Daten konnten nicht geladen werden.", true),
      );
      if (state.motif.required) {
        setMotifState("error", { selection: null, error });
      }
      console.error("Baurelia Designer: Startdaten konnten nicht geladen werden.", error);
    }
  };

  const ensureMotifReady = async () => {
    if (!state.motif.required) return null;
    if (state.motif.status === "loading") await state.motif.promise;

    const selection = state.motif.selection;
    const checked = document.querySelector('input[name="properties[Motiv]"]:checked');
    const preview = document.querySelector(
      '[id="dynamic-image"][data-baurelia-motif-id], #canvas-image[data-baurelia-motif-id]',
    );
    if (
      state.motif.status !== "ready" ||
      !selection?.id ||
      checked?.dataset.metaobjectId !== selection.id ||
      preview?.dataset.baureliaMotifId !== selection.id
    ) {
      throw new Error(
        "Das ausgewählte Motiv ist noch nicht vollständig geladen. Bitte erneut versuchen.",
      );
    }
    return selection;
  };

  window.BaureliaCustomizerCatalog = {
    init,
    renderMotifs,
    ensureMotifReady,
    canSubmit: () => !state.motif.required || state.motif.status === "ready",
    getMotifState: () => ({
      required: state.motif.required,
      status: state.motif.status,
      selection: state.motif.selection,
      error: state.motif.error?.message || null,
    }),
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
